import { useMemo, useState } from "react";
import { useLocation } from "wouter";
import { trpc } from "@/lib/trpc";
import { AlertTriangle } from "lucide-react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";

export function DeadlineAlertBell() {
  const [open, setOpen] = useState(false);
  const [, navigate] = useLocation();

  const { data: rawAlerts = [] } = trpc.kanban.deadlineAlerts.useQuery(
    undefined,
    {
      refetchInterval: 60_000,
    }
  );

  const alerts = useMemo(() => {
    const now = new Date();
    const overdue: typeof rawAlerts = [];
    const expiring3: typeof rawAlerts = [];
    for (const v of rawAlerts) {
      const updated = new Date(v.vacancy.updatedAt);
      const created = new Date(v.vacancy.createdAt);
      const deadlineWasUpdated = updated.getTime() - created.getTime() > 60_000;
      const updatedToday =
        now.getTime() - updated.getTime() < 24 * 60 * 60 * 1000;
      if (deadlineWasUpdated && updatedToday) continue;
      const dl = new Date(v.vacancy.deadlineDate);
      const daysLeft = Math.ceil(
        (dl.getTime() - now.getTime()) / (1000 * 60 * 60 * 24)
      );
      if (daysLeft < 0) overdue.push(v);
      else if (daysLeft <= 3) expiring3.push(v);
    }
    return { overdue, expiring3 };
  }, [rawAlerts]);

  const totalAlerts = alerts.overdue.length + alerts.expiring3.length;

  if (totalAlerts === 0) return null;

  const hasOverdue = alerts.overdue.length > 0;

  const handleClick = (vacancyId: number) => {
    navigate(`/kanban?vacancy=${vacancyId}`);
    window.dispatchEvent(
      new CustomEvent("open-vacancy", { detail: { vacancyId } })
    );
    setOpen(false);
  };

  const renderItem = (
    v: (typeof rawAlerts)[0],
    colorClass: string,
    label: string
  ) => {
    const recruiterName = v.recruiter?.displayName || v.recruiter?.name || "—";
    const contractDate = v.contract?.contractDate
      ? new Date(v.contract.contractDate)
      : null;
    const daysInWork = contractDate
      ? Math.floor((Date.now() - contractDate.getTime()) / 86400000)
      : 0;
    const fmtDate = contractDate
      ? `${contractDate.getDate().toString().padStart(2, "0")}.${(contractDate.getMonth() + 1).toString().padStart(2, "0")}`
      : "—";
    return (
      <button
        key={v.vacancy.id}
        onClick={() => handleClick(v.vacancy.id)}
        className={`w-full flex items-center gap-2 px-2 py-1.5 rounded-md hover:bg-slate-50 text-left transition-colors`}
      >
        <div className="flex-1 min-w-0">
          <p className="text-xs font-medium text-slate-800">
            {v.vacancy.positionTitle}
          </p>
          <p className="text-[11px] text-slate-500">
            {v.company?.name ?? "—"} ·{" "}
            <span className="font-semibold text-slate-700">
              {recruiterName}
            </span>
          </p>
          <p className="text-[10px] text-slate-400">
            от {fmtDate} · {daysInWork} дн. в работе
          </p>
        </div>
        <span className={`text-[11px] font-semibold shrink-0 ${colorClass}`}>
          {label}
        </span>
      </button>
    );
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="relative size-8 rounded-lg hover:bg-neutral-100"
        >
          <AlertTriangle
            className={`size-4 ${hasOverdue ? "text-red-500" : "text-amber-500"}`}
          />
          <span className="absolute right-1 top-1 flex items-center justify-center rounded-full bg-red-500 text-[9px] font-semibold text-white min-w-[14px] h-3.5 px-0.5 border border-white">
            {totalAlerts > 99 ? "99+" : totalAlerts}
          </span>
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[min(420px,calc(100vw-24px))] p-0 max-h-[50vh] overflow-hidden flex flex-col rounded-2xl shadow-[0_8px_30px_-4px_rgba(0,0,0,0.12)] border-0"
        align="end"
      >
        <div className="px-4 py-3 bg-slate-50/60 shrink-0 rounded-t-2xl">
          <h3 className="font-semibold text-sm text-slate-900">
            Дедлайны вакансий
          </h3>
        </div>
        <ScrollArea className="flex-1 overflow-auto">
          {alerts.overdue.length > 0 && (
            <div className="p-2">
              <p className="text-[11px] font-semibold text-red-600 px-2 py-1">
                Просрочено ({alerts.overdue.length})
              </p>
              {alerts.overdue.map((v: any) => {
                const d = Math.abs(
                  Math.ceil(
                    (new Date(v.vacancy.deadlineDate).getTime() - Date.now()) /
                      86400000
                  )
                );
                return renderItem(v, "text-red-500", `-${d}д`);
              })}
            </div>
          )}
          {alerts.expiring3.length > 0 && (
            <div
              className={`p-2 ${alerts.overdue.length > 0 ? "border-t" : ""}`}
            >
              <p className="text-[11px] font-semibold text-amber-600 px-2 py-1">
                Через 1-3 дня ({alerts.expiring3.length})
              </p>
              {alerts.expiring3.map((v: any) => {
                const d = Math.ceil(
                  (new Date(v.vacancy.deadlineDate).getTime() - Date.now()) /
                    86400000
                );
                return renderItem(v, "text-amber-500", `${d}д`);
              })}
            </div>
          )}
        </ScrollArea>
      </PopoverContent>
    </Popover>
  );
}
